import { ChangeEvent, FC, FormEvent, useEffect, useState } from 'react';

import { Box, Button, Dialog, Input, Select, Typography } from '@robocorp/components';
import { IconCheckmark } from '@robocorp/icons/iconic';
import { OrganizationSLAState } from '@robocorp/robocloud-internal-cds-sdk/lib/src/types/RcInstanceDB';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { AppState } from '~/store';
import { serviceDeskActions, uiActions } from '../actions';
import { useCurrentOrFirstOrganization, useOrganizationsWithSLA } from '../queries';
import { AvatarIcon } from './old-ds-components';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = StateProps & DispatchProps;

const ServiceDeskDialog: FC<Props> = ({
  serviceDeskDialogOpen,
  toggleServiceDeskDialog,
  loading,
  error,
  currentUser,
  sendServiceTicket,
}) => {
  const { t } = useTranslation();

  const { data: currentOrganization } = useCurrentOrFirstOrganization();

  const { data: organizationsWithSLA = [] } = useOrganizationsWithSLA();

  // Construct select items from organizations with SLA
  const organizationSelectItems = organizationsWithSLA.map(({ id, name }) => ({
    value: id,
    label: name,
  }));

  // Form states
  const [subject, setSubject] = useState<string>('');
  const [content, setContent] = useState<string>('');
  const [organizationSLA, setOrganizationSLA] = useState<OrganizationSLAState>(
    OrganizationSLAState.NONE,
  );
  const [selectedOrganization, setSelectedOrganization] = useState<string>('');
  const [formSubmitted, changeFormSubmited] = useState<boolean>(false);

  // Set default selected organization value
  useEffect(() => {
    if (!currentOrganization) return;
    setSelectedOrganization(currentOrganization.id);
    setOrganizationSLA(currentOrganization?.limit_overrides.sla || OrganizationSLAState.NONE);
  }, [currentOrganization]);

  const clearForm = () => {
    changeFormSubmited(false);
    setSubject('');
    setContent('');
    setOrganizationSLA(OrganizationSLAState.NONE);
    if (currentOrganization) {
      setSelectedOrganization(currentOrganization.id);
      setOrganizationSLA(currentOrganization?.limit_overrides.sla || OrganizationSLAState.NONE);
    }
  };

  useEffect(() => {
    if (formSubmitted) {
      const closeTimeOut = window.setTimeout(() => toggleServiceDeskDialog(), 2000);
      return () => {
        clearTimeout(closeTimeOut);
      };
    }

    return undefined;
  }, [formSubmitted]);

  useEffect(() => {
    if (!serviceDeskDialogOpen) {
      const clearFormTimeOut = window.setTimeout(() => clearForm(), 1000);
      return () => {
        clearTimeout(clearFormTimeOut);
      };
    }

    return undefined;
  }, [serviceDeskDialogOpen]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (selectedOrganization) {
      const email = currentUser.details.email ?? '';
      const response = await sendServiceTicket(
        selectedOrganization,
        email,
        subject,
        content,
        organizationSLA,
      );
      if (response.ok) {
        changeFormSubmited(true);
      }
    }
  };

  const onChangeSubject = (e: ChangeEvent<HTMLInputElement>) => {
    setSubject(e.target.value);
  };

  const onChangeContent = (e: ChangeEvent<HTMLInputElement>) => {
    setContent(e.target.value);
  };

  const onChangeOrganization = (organizationId: string) => {
    setSelectedOrganization(organizationId);
    if (currentOrganization) {
      setOrganizationSLA(currentOrganization.limit_overrides.sla || OrganizationSLAState.NONE);
    }
  };

  const getSLAText = () => {
    switch (organizationSLA) {
      case OrganizationSLAState.ONE_DAY: {
        return t('ticketSentDescOneDaySLA');
      }
      case OrganizationSLAState.BEST_EFFORT: {
        return t('ticketSentDescSLA');
      }
      case OrganizationSLAState.NONE: {
        return t('ticketSentNoSLA');
      }
      default:
        return t('ticketSentNoSLA');
    }
  };

  const getDialogTitle = () => {
    if (!formSubmitted) {
      if (organizationSLA === OrganizationSLAState.NONE) {
        return t('reportIssue');
      }
      return t('createSupportTicket');
    }
    return undefined;
  };

  return (
    <Dialog
      open={serviceDeskDialogOpen}
      onClose={toggleServiceDeskDialog}
      width={!formSubmitted ? 615 : 380}
    >
      <Dialog.Header>
        <Dialog.Header.Title title={getDialogTitle()} />
      </Dialog.Header>
      {formSubmitted && !loading && !error && (
        <Dialog.Content>
          <Box p={[20, 40]} textAlign="center">
            <Box mb={16}>
              <AvatarIcon size="large" placeholder={<IconCheckmark color="green70" />} />
            </Box>
            <Typography as="h2" mb={8}>
              {t('ticketSent')}
            </Typography>
            <Typography color="grey20">{getSLAText()}</Typography>
          </Box>
        </Dialog.Content>
      )}
      {!formSubmitted && (
        <Dialog.Content>
          <form onSubmit={onSubmit}>
            <Box pb={28}>
              <Select
                label={t('organization')}
                items={organizationSelectItems}
                value={selectedOrganization}
                onChange={onChangeOrganization}
                // If only one paying org or less, disable selection
                disabled={organizationSelectItems.length < 2}
              />
            </Box>
            <Box pb={28}>
              <Input
                label={t('subject')}
                description={t('subjectDesc')}
                value={subject}
                onChange={onChangeSubject}
                required
              />
            </Box>
            <Box>
              <Input
                label={t('description')}
                description={
                  currentOrganization?.limit_overrides.sla === OrganizationSLAState.NONE
                    ? ''
                    : t('ticketResponseDesc')
                }
                placeholder={t('ticketProblemDesc')}
                value={content}
                onChange={onChangeContent}
                rows={5}
                required
              />
            </Box>

            <Box>
              {error && (
                <Box pt={10}>
                  <Typography fontSize="$12" color="content.error">
                    {t('errorSendingTicket')}
                  </Typography>
                </Box>
              )}
            </Box>
          </form>
        </Dialog.Content>
      )}
      {!formSubmitted && (
        <Dialog.Actions>
          <Button
            type="button"
            onClick={onSubmit}
            disabled={!selectedOrganization}
            loading={loading}
          >
            {t('create')}
          </Button>
          <Button type="button" onClick={toggleServiceDeskDialog} variant="secondary">
            {t('cancel')}
          </Button>
        </Dialog.Actions>
      )}
    </Dialog>
  );
};

function mapStateToProps(state: AppState) {
  const { serviceDeskDialogOpen } = state.ui;
  const { loading, error } = state.entityStates.serviceDesk;

  return {
    serviceDeskDialogOpen,
    loading,
    error,
    currentUser: state.userResources.users.current,
  };
}

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      toggleServiceDeskDialog: uiActions.toggleServiceDeskDialog,
      sendServiceTicket: serviceDeskActions.sendServiceTicket,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(ServiceDeskDialog);
